import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, CheckCheck, MessageCircle, MessageCircleOff, PenSquare, Search, Send, UserRound } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import { SUPPORT_PROJECT, useSupport } from '../Context/SupportContext';
import Loader from '../components/Loader';
import { Badge, Empty, Status, Toast } from '../components/userProject/UpUi';
import { ago, dateTime } from '../components/userProject/format';
import UpUserPanel from '../components/userProject/UpUserPanel';
import { projectApi } from '../components/userProject/api';

const MAX_LEN = 1000
const blocked = (u) => u.status === 'BLOCKED' || (u.suspendedUntil && new Date(u.suspendedUntil) > new Date())

/**
 * Discussions avec les users de PrimeStation (tout est chez PrimeStation : table SupportMessage, /internal/admin/support).
 * L'admin écrit à n'importe quel compte (« Écrire à un user ») : il le reçoit tout de suite (menu « Support » du site).
 * C'est l'admin qui décide qui peut répondre : comptes bloqués oui par défaut (page « Compte bloqué »), sauf demande
 * clôturée ; les autres seulement s'il les y autorise. En direct (Context/SupportContext) : nouveaux messages, « lu ».
 * Ouvrir une discussion marque ses messages « lus » (le user le voit) ; le user voit « Support », jamais le nom de l'admin.
 */
const PlatformSupport = () => {
    const { setCurrentPage } = useContext(AppContext)
    const { on, connected, refresh } = useSupport()
    const [params, setParams] = useSearchParams()
    const pApi = useMemo(() => projectApi(SUPPORT_PROJECT), [])
    const [filter, setFilter] = useState('')
    const [search, setSearch] = useState('')
    const [q, setQ] = useState('')
    const [threads, setThreads] = useState(null)
    const [error, setError] = useState('')
    const openId = Number(params.get('user')) || null
    const openRef = useRef(openId)
    openRef.current = openId
    const [thread, setThread] = useState(null)
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)
    const [toast, setToast] = useState(null)
    const [fiche, setFiche] = useState(null) // id du user dont la fiche est ouverte
    const [find, setFind] = useState('') // « Écrire à un user » : recherche dans tous les comptes
    const [found, setFound] = useState(null)
    const listRef = useRef()
    const notify = useCallback((t, isError = false) => setToast({ text: t, error: isError, at: Date.now() }), [])

    useEffect(() => { setCurrentPage('support_plateformes') }, [setCurrentPage])

    const loadThreads = useCallback(async () => {
        try {
            const r = await pApi.get('/support', { ...(filter === 'unread' && { unread: 1 }), ...(q && { q }) })
            setThreads(r.threads)
            setError('')
        } catch (e) { setError(e.message) }
    }, [pApi, filter, q])
    useEffect(() => { loadThreads() }, [loadThreads])
    useEffect(() => {
        const t = setTimeout(() => setQ(search.trim()), 350)
        return () => clearTimeout(t)
    }, [search])

    // « Écrire à un user » : n'importe quel compte, par email (même sans discussion)
    useEffect(() => {
        const q = find.trim()
        if (q.length < 2) return setFound(null)
        const t = setTimeout(async () => {
            try { setFound((await pApi.get('/users', { q, perPage: 8 })).items) } catch (e) { setFound([]) }
        }, 300)
        return () => clearTimeout(t)
    }, [find, pApi])

    // ouvrir un fil le marque « lu » (côté PrimeStation) : pastilles du menu et de la liste relues tout de suite
    // (sans attendre le direct, au cas où le socket ne serait pas branché)
    const loadThread = useCallback(async (id) => {
        try {
            setThread(await pApi.get(`/support/${id}`))
            refresh()
            setThreads((list) => list?.map((t) => (t.user.id === id ? { ...t, unread: 0 } : t)))
        } catch (e) { notify(e.message, true); setThread(null) }
    }, [pApi, notify, refresh])
    useEffect(() => {
        setThread(null)
        setText('')
        if (openId) loadThread(openId)
    }, [openId, loadThread])
    useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }) }, [thread?.messages?.length])

    // en direct : liste relue ; fil ouvert → message ajouté (celui d'un user est relu pour passer « lu »), « lu » par le user
    useEffect(() => on((type, data) => {
        loadThreads()
        if (!data || data.userId !== openRef.current) return
        if (type === 'message' && !data.message.fromAdmin) return loadThread(data.userId)
        if (type === 'message') return setThread((t) => (t && !t.messages.some((m) => m.id === data.message.id) ? { ...t, messages: [...t.messages, data.message] } : t))
        if (type === 'read' && data.by === 'user') setThread((t) => t && { ...t, messages: t.messages.map((m) => (m.fromAdmin && !m.readAt ? { ...m, readAt: data.at } : m)) })
    }), [on, loadThreads, loadThread])

    const open = (id) => setParams(id ? { user: String(id) } : {})

    const send = async (e) => {
        e?.preventDefault()
        const t = text.trim()
        if (!t || sending || !openId) return
        setSending(true)
        try {
            const r = await pApi.post(`/support/${openId}`, { text: t })
            setText('')
            setThread((th) => (th && !th.messages.some((m) => m.id === r.message.id) ? { ...th, messages: [...th.messages, r.message] } : th))
            loadThreads()
        } catch (err) { notify(err.message, true) }
        setSending(false)
    }

    // peut répondre ou non (bloqué : demande clôturée / rouverte ; sinon discussion autorisée ou non)
    const toggleChat = async () => {
        const u = thread.user
        try {
            const r = await pApi.post(`/users/${u.id}/chat`, { enabled: !u.canChat })
            notify(r.result || 'Fait')
            await loadThread(u.id)
            loadThreads()
        } catch (err) { notify(err.message, true) }
    }

    const u = thread?.user
    const lastAdmin = thread?.messages?.findLast((m) => m.fromAdmin)?.id

    return (
        <MainLayout>
            <CenterLayout>
                <div className='up-page sup-page'>
                    <header className='up-head'>
                        <div>
                            <h1>Support plateformes</h1>
                            <p className='up-muted'>
                                Discussions avec les users de PrimeStation : comptes bloqués (« Parler au support ») et comptes dont la discussion est ouverte.
                                {' '}{connected ? <Badge tone='live'>En direct</Badge> : <Badge>Hors ligne</Badge>}
                            </p>
                        </div>
                    </header>

                    <div className={`sup-grid ${openId ? 'has-open' : ''}`}>
                        <section className='sup-threads'>
                            <div className='sup-tools'>
                                <div className='sup-new'>
                                    <div className='up-search'>
                                        <PenSquare size={16} />
                                        <input value={find} onChange={(e) => setFind(e.target.value)} placeholder='Écrire à un user (email)…' />
                                    </div>
                                    {found && (
                                        <div className='sup-found'>
                                            {!found.length && <p className='up-muted'>Aucun compte</p>}
                                            {found.map((x) => (
                                                <button key={x.id} onClick={() => { open(x.id); setFind('') }}>
                                                    <b>{x.email}</b>
                                                    <Status status={x.status} suspendedUntil={x.suspendedUntil} />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className='up-search'>
                                    <Search size={16} />
                                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Filtrer les discussions…' />
                                </div>
                                <div className='sup-filters'>
                                    {[['', 'Toutes'], ['unread', 'Non lues']].map(([k, label]) => (
                                        <button key={k} className={`sup-filter ${filter === k ? 'on' : ''}`} onClick={() => setFilter(k)}>{label}</button>
                                    ))}
                                </div>
                            </div>
                            {error && <p className='up-error'>{error}</p>}
                            {!threads && !error && <div className='up-loading'><Loader /></div>}
                            {threads && !threads.length && <Empty>{filter === 'unread' ? 'Aucun message non lu' : 'Aucune discussion pour le moment'}</Empty>}
                            {threads?.map((t) => (
                                <button key={t.user.id} className={`sup-thread ${openId === t.user.id ? 'on' : ''} ${t.unread ? 'unread' : ''}`} onClick={() => open(t.user.id)}>
                                    <span className='sup-thread-top'>
                                        <b>{t.user.email}</b>
                                        <small>{ago(t.last?.at)}</small>
                                    </span>
                                    <span className='sup-thread-last'>{t.last?.fromAdmin ? 'Vous : ' : ''}{t.last?.text}</span>
                                    <span className='sup-thread-tags'>
                                        {blocked(t.user) && <Status status={t.user.status} suspendedUntil={t.user.suspendedUntil} />}
                                        {!t.canChat && <Badge>Discussion fermée</Badge>}
                                        {t.unread > 0 && <span className='sup-count'>{t.unread}</span>}
                                    </span>
                                </button>
                            ))}
                        </section>

                        <section className='sup-conv'>
                            {!openId && <Empty>Choisis une discussion</Empty>}
                            {openId && !thread && <div className='up-loading'><Loader /></div>}
                            {u && (
                                <>
                                    <header className='sup-conv-head'>
                                        <button className='pay-act-btn sup-back' onClick={() => open(null)} title='Retour'><ArrowLeft size={18} color='#fff' /></button>
                                        <div className='sup-conv-who'>
                                            <b>{u.email}</b>
                                            <span className='up-badges'>
                                                <span className='up-muted'>#{u.id}</span>
                                                <Status status={u.status} suspendedUntil={u.suspendedUntil} />
                                                {u.canChat ? <Badge tone='ok'>Peut répondre</Badge> : blocked(u) && u.chatMuted ? <Badge tone='ko'>Demande clôturée</Badge> : <Badge>Lecture seule</Badge>}
                                            </span>
                                        </div>
                                        <div className='sup-conv-actions'>
                                            <button
                                                className={`btn ${u.canChat ? 'up-danger-soft' : 'cs'}`}
                                                onClick={toggleChat}
                                                title={blocked(u) ? 'Compte bloqué : il peut laisser un message, sauf si tu clôtures sa demande' : 'Il lit tes messages ; il ne répond que si tu l’y autorises'}
                                            >
                                                {u.canChat
                                                    ? <><MessageCircleOff size={15} /> {blocked(u) ? 'Clôturer sa demande' : 'Ne plus l’autoriser à répondre'}</>
                                                    : <><MessageCircle size={15} /> {blocked(u) ? 'Rouvrir sa demande' : 'L’autoriser à répondre'}</>}
                                            </button>
                                            <button className='btn cw' onClick={() => setFiche(u.id)}><UserRound size={15} /> Fiche</button>
                                        </div>
                                    </header>

                                    <div className='sup-msgs' ref={listRef}>
                                        {!thread.messages.length && <Empty>Pas encore de message. Écris-lui : il le reçoit tout de suite dans « Support » (menu de son compte).</Empty>}
                                        {thread.messages.map((m) => (
                                            <div key={m.id} className={`sup-msg ${m.fromAdmin ? 'me' : 'them'}`}>
                                                <p>{m.text}</p>
                                                <span className='sup-meta'>
                                                    {m.fromAdmin && m.admin ? `${m.admin} · ` : ''}{dateTime(m.at)}
                                                    {m.fromAdmin && (m.readAt ? <CheckCheck size={14} className='read' /> : <Check size={14} />)}
                                                </span>
                                                {m.id === lastAdmin && m.readAt && <span className='sup-seen'>Lu par le user {ago(m.readAt)}</span>}
                                            </div>
                                        ))}
                                    </div>

                                    <form className='sup-reply' onSubmit={send}>
                                        <textarea
                                            value={text}
                                            maxLength={MAX_LEN}
                                            rows={2}
                                            placeholder='Ta réponse… (Entrée pour envoyer)'
                                            title='Entrée : envoyer · Maj+Entrée : aller à la ligne'
                                            onChange={(e) => setText(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && send(e)}
                                        />
                                        <button className='btn cs' disabled={!text.trim() || sending}><Send size={15} /> {sending ? 'Envoi…' : 'Envoyer'}</button>
                                    </form>
                                    {!u.canChat && <p className='up-muted sup-hint'>{blocked(u) && u.chatMuted ? 'Demande clôturée : il relit la discussion mais ne peut plus écrire.' : 'Il lit tes messages mais ne peut pas répondre tant que tu ne l’y autorises pas.'}</p>}
                                </>
                            )}
                        </section>
                    </div>
                </div>
                {fiche && (
                    <UpUserPanel pApi={pApi} userId={fiche} onClose={() => setFiche(null)} onOpen={setFiche} notify={notify} onChanged={() => { if (openId) loadThread(openId); loadThreads() }} />
                )}
                <Toast toast={toast} onDone={() => setToast(null)} />
            </CenterLayout>
        </MainLayout>
    );
};

export default PlatformSupport;
